import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AdminTab } from '../types/admin';
import { CloudOnboardingTab } from '../components/admin/CloudOnboardingTab';
import { UserManagementTab } from '../components/admin/UserManagementTab';
import { IntegrationsTab } from '../components/admin/IntegrationsTab';
import { IngestionHealthTab } from '../components/admin/IngestionHealthTab';
import { AIConfigTab } from '../components/admin/AIConfigTab';
import { FeatureFlagsTab } from '../components/admin/FeatureFlagsTab';
import { BudgetSettingsTab } from '../components/admin/BudgetSettingsTab';
import { ChangeWindowsTab } from '../components/admin/ChangeWindowsTab';
import { AnomalyModelHealthTab } from '../components/admin/AnomalyModelHealthTab';
import { PlatformAnomaliesTab } from '../components/admin/PlatformAnomaliesTab';
// Demo-scenario injection deliberately does NOT live in this console any more.
// It is a standalone page at /demo-scenarios so it can be driven from a second
// browser tab while the app under demo stays untouched in the first.
import { TenantSwitcher } from '../components/admin/TenantSwitcher';

/**
 * Admin Console Page
 * Implements Requirement 82: Web UI — Admin Console [MVP]
 * 
 * AC 1: Support tenant configuration: cloud account onboarding wizards, user management, integration setup
 * AC 2: Display ingestion health per cloud account
 * AC 3: Provide AI configuration: tier enablement, kill switch, per-feature consent, cost caps
 * AC 4: Display feature-flag status
 * 
 * WCAG 2.1 AA compliant
 */
export const AdminConsolePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>(AdminTab.CLOUD_ONBOARDING);
  // Deep link support, e.g. /admin?tab=budget_settings - lets other pages send
  // the user to a specific admin tab instead of dumping them on Cloud
  // Onboarding and making them hunt.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && Object.values(AdminTab).includes(t as AdminTab)) {
      setActiveTab(t as AdminTab);
    }
  }, [searchParams]);

  const tabs = [
    {
      id: AdminTab.CLOUD_ONBOARDING,
      label: 'Cloud Onboarding',
      icon: (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"
          />
        </svg>
      ),
    },
    {
      id: AdminTab.USER_MANAGEMENT,
      label: 'User Management',
      icon: (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ),
    },
    {
      id: AdminTab.INTEGRATIONS,
      label: 'Integrations',
      icon: (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
          />
        </svg>
      ),
    },
    {
      id: AdminTab.INGESTION_HEALTH,
      label: 'Ingestion Health',
      icon: (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
    {
      id: AdminTab.AI_CONFIG,
      label: 'AI Configuration',
      icon: (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      ),
    },
    {
      id: AdminTab.FEATURE_FLAGS,
      label: 'Feature Flags',
      icon: (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
          />
        </svg>
      ),
    },
    {
      id: AdminTab.BUDGET_SETTINGS,
      label: 'Budget Settings',
      icon: (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      id: AdminTab.CHANGE_WINDOWS,
      label: 'Change Windows',
      icon: (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      id: AdminTab.ANOMALY_MODEL_HEALTH,
      label: 'Anomaly Model Health',
      icon: (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      id: AdminTab.PLATFORM_ANOMALIES,
      label: 'Platform Anomalies',
      icon: (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0zM12 8v4l3 3" />
        </svg>
      ),
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case AdminTab.CLOUD_ONBOARDING:
        return <CloudOnboardingTab />;
      case AdminTab.USER_MANAGEMENT:
        return <UserManagementTab />;
      case AdminTab.INTEGRATIONS:
        return <IntegrationsTab />;
      case AdminTab.INGESTION_HEALTH:
        return <IngestionHealthTab />;
      case AdminTab.AI_CONFIG:
        return <AIConfigTab />;
      case AdminTab.FEATURE_FLAGS:
        return <FeatureFlagsTab />;
      case AdminTab.BUDGET_SETTINGS:
        return <BudgetSettingsTab />;
      case AdminTab.CHANGE_WINDOWS:
        return <ChangeWindowsTab />;
      case AdminTab.ANOMALY_MODEL_HEALTH:
        return <AnomalyModelHealthTab />;
      case AdminTab.PLATFORM_ANOMALIES:
        return <PlatformAnomaliesTab />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Tenant switcher gets its own right-aligned row ABOVE the tabs.
          It briefly shared the tab row, which does not work: there are eleven
          tabs and that nav already scrolls horizontally, so a control competing
          for the same width ended up overlapping the last tab label. A row of
          its own costs a little vertical space and cannot collide. */}
      <div className="flex justify-end">
        <TenantSwitcher />
      </div>

      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-6 overflow-x-auto" aria-label="Admin tabs">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-2.5 text-xs font-medium
                  ${
                    isActive
                      ? 'border-primary/30 text-primary dark:border-primary/30 dark:text-primary'
                      : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground/80 dark:text-muted-foreground/70 dark:hover:border-border dark:hover:text-foreground'
                  }
                  focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
                `}
                aria-current={isActive ? 'page' : undefined}
              >
                {tab.icon}
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="pt-4">{renderTabContent()}</div>
    </div>
  );
};
